import React, { useEffect, useState } from 'react';
import { Button, Flex, Text, Alert, View, Link } from '@smarterservices/smarter-ui';
import { SpeechNotSupportedError } from '@session/helpers/definedErrors';
import { getOperatingSystem } from '@session/helpers/helpers';
import { VerificationInput } from '../VerificationInput';
import DualPane from '../DualPane';
import { usePaneViewer } from '../PaneViewer';
import StepSuccessMessage from '../Common/StepSuccessMessage';
import StepInstructions from '../Common/StepInstructions';

const SpeakerCheck = ({ stepData, setIsTaskComplete, setStepMetadata }) => {
  const [code, setCode] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOptingOut, setIsOptingOut] = useState(false);
  const [isHearingImpared, setIsHearingImpared] = useState(false);
  const [cannotHear, setCannotHear] = useState(false);
  const [hasAudioIssues, setHasAudioIssues] = useState(false);
  const { onNext } = usePaneViewer();

  useEffect(() => {
    const generateCode = () => {
      // Generate a 4-digit code without the digit 8
      const allowedDigits = [0, 1, 2, 3, 4, 5, 6, 7, 9];
      let code = '';

      // Generate all 4 digits randomly
      for (let i = 0; i < 4; i++) {
        code += allowedDigits[Math.floor(Math.random() * allowedDigits.length)];
      }

      return code; // Return as string to preserve leading zeros
    };
    const newCode = generateCode();
    setCode(newCode);
  }, []);

  const readCodeAloud = () => {
    setErrorMessage('');
    if (code !== null) {
      if ('speechSynthesis' in window) {
        // Split out the code so the reader says each number instead of reading it as a number in the thousands
        const digitString = code.toString().split('').join('. ');
        const utterance = new SpeechSynthesisUtterance(
          `${stepData?.yourCodeIs || 'Your code is'} ${digitString}`
        );
        window.speechSynthesis.speak(utterance);
        setIsSpeaking(true);
        setIsListening(true);


        // When the speech is finished, update the state
        utterance.onend = () => {
          setIsSpeaking(false);
        };

        // Auto focus the first input after playing the code
        setTimeout(() => {
          const firstInput = document.querySelector('input');
          if (firstInput) firstInput.focus();
        }, 100);
      } else {
        // TODO: instead of throwing, what else should we do??
        throw new SpeechNotSupportedError();
      }
    }
  };

  const handleSubmit = (finalValue = userInput) => {
    if (finalValue === code.toString()) {
      setErrorMessage('');
      setIsSuccess(true);
      setStepMetadata({
        speakerCheckResult: "success",
        isHearingImpared,
        code
      });
    } else {
      setErrorMessage(stepData?.errorMessage);
      setUserInput(''); // Clear input on error
      // Focus the first input after error
      const firstInput = document.querySelector('input');
      if (firstInput) firstInput.focus();
    }
  };

  const handleOptOut = () => {
    setStepMetadata({
      speakerCheckResult: "opt-out",
      isHearingImpared,
    });
    handleMoveToNextStep();
  };

  const handleMoveToNextStep = () => {
    setIsTaskComplete(true);
    onNext();
  };

  const RenderCannotHearCodeLink = () => {
    if(stepData?.allowOptOut || stepData?.allowOptOut === undefined){
      return (
        <Link
          as="button"
          onClick={() => {
            setErrorMessage('');
            setIsListening(false);
            setCannotHear(true);
          }}
          isWithinText={false}
          margin="0"
        >
          {stepData?.cannotHearLink}
        </Link>
      );
    }
    return null;
  };

  const RenderCannotHear = () => {
    if(stepData?.allowOptOut || stepData?.allowOptOut === undefined){
      return (
        <Flex direction="column" alignItems="center" gap="medium">
          <Text variant="descriptionPage">{stepData?.hearingImparedPromptText}</Text>
          <Flex direction="row" alignItems="center" gap="medium">
            <Button color="primary" onClick={() => setIsHearingImpared(true)}>
              {stepData?.hearingImparedPromptButtonYes}
            </Button>
            <Button color="primary" onClick={() => setHasAudioIssues(true)}>
              {stepData?.hearingImparedPromptButtonNo}
            </Button>
          </Flex>
        </Flex>
      );
    }
    return null;
  };

  const RenderHearingChallenge = () => {
    return (
      <Flex direction="column" alignItems="center" gap="medium">
        {!isListening ? (
          <Flex direction="column" alignItems="center" gap="medium">
            <Button color="primary" onClick={readCodeAloud}>
              {stepData.playButton}
            </Button>
            <RenderCannotHearCodeLink />
          </Flex>
        ) : (
          <>
            <Flex direction="column" alignItems="center" minHeight="48px">
              {errorMessage && (
                <Alert
                  variant="error"
                  margin="none none medium none"
                  variantScreenReaderLabel="Error Alert"
                >
                  {errorMessage}
                </Alert>
              )}
              {isSpeaking && (
                <Alert
                  variant="info"
                  margin="none none medium none"
                  variantScreenReaderLabel="Info Alert"
                >
                  {stepData?.speakingCodeText}
                </Alert>
              )}
              {!isSuccess && (
                <View margin="0 0 medium 0">
                  <VerificationInput
                    value={userInput}
                    onChange={setUserInput}
                    onComplete={handleSubmit}
                  />
                </View>
              )}

              {isSuccess && (
                <>
                  <StepSuccessMessage message={stepData.successMessage} />
                  <Button
                    color="primary"
                    variant="text"
                    onClick={handleMoveToNextStep}
                    margin="medium 0"
                  >
                    {stepData?.nextStepButton}
                  </Button>
                </>
              )}
              {!isSuccess && !isSpeaking && (
                <Flex direction="column" alignItems="center" gap="medium">
                  <Button
                    color="primary"
                    variant="text"
                    onClick={readCodeAloud}

                  >
                    {stepData?.playAgainButton}
                  </Button>
                  <RenderCannotHearCodeLink />
                </Flex>
              )}

            </Flex>
          </>
        )}
      </Flex>
    );

  };

  const RenderAudioIssues = () => {
    const os = getOperatingSystem(window).toLowerCase();
    return (
      <Flex direction="column" textAlign="center" alignItems="center" gap="medium">
        <Text>
          <StepInstructions instructions={stepData?.helpText} />
        </Text>
        <Button
          color="primary"
          onClick={() => {
            const url = stepData?.helpUrl?.[os] || stepData?.helpUrl?.default;
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          margin="small 0"
        >
          {stepData?.helpButton}
        </Button>

        <Link
          as="button"
          onClick={resetStep}
          isWithinText={false}
        >
          {stepData?.tryAgainText}
        </Link>
      </Flex>
    );
  };

  const RenderHearingImpared = () => {
    return (
      <Flex direction="column" textAlign="center" alignItems="center" gap="medium">
        <Text>
          <StepInstructions instructions={stepData?.hearingImparedOptOutText} />
        </Text>
        <Button
          color="primary"
          onClick={handleOptOut}
          margin="small 0"
        >
          {stepData?.hearingImparedOptOutButton}
        </Button>

        <Link
          as="button"
          onClick={resetStep}
          isWithinText={false}
        >
          {stepData?.hearingImparedOptOutCancel}
        </Link>
      </Flex>
    );
  };

  const RenderStepContent = () => {
    if (cannotHear) {
      if(hasAudioIssues){
        return RenderAudioIssues();
      }else if(isHearingImpared){
        return RenderHearingImpared();
      }
      return RenderCannotHear();
    }
    return RenderHearingChallenge();
  };

  const resetStep = () => {
    setCannotHear(false);
    setHasAudioIssues(false);
    setIsHearingImpared(false);
  };

  return (
    <DualPane
      leftPane={
        <StepInstructions instructions={stepData?.body} />
      }
      rightPane={
        <Flex
          direction="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          {RenderStepContent()}
        </Flex>
      }
    />
  );
};
export default SpeakerCheck;